import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';
import { useAuth } from '../../contexts/AuthContext';
import '../styles/AnalyticsScreen.css';
import { useLocation } from 'react-router-dom';

const AnalyticsScreen = ({ setActiveScreen }) => {
  const { getAllAnalytics, getAnalyticsTotals, getAllStandoutAnalytics, getStandoutAnalyticsTotals, loading } = useAuth();

  const [activeItem, setActiveItem] = useState('analytics');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [analytics, setAnalytics] = useState([]);
  const [standoutAnalytics, setStandoutAnalytics] = useState([]);
  const location=useLocation();
  const [totals, setTotals] = useState({
    views: 0, unique_views: 0,
    shares: 0, unique_shares: 0,
    link_copies: 0, unique_link_copies: 0,
    caption_copies: 0, unique_caption_copies: 0,
    downloads: 0, unique_downloads: 0
  });
  const [standoutTotals, setStandoutTotals] = useState({
    views: 0, unique_views: 0,
    shares: 0, unique_shares: 0,
    link_copies: 0, unique_link_copies: 0,
    downloads: 0, unique_downloads: 0
  });
  const [filterBy, setFilterBy] = useState('name');
  const [standoutFilterBy, setStandoutFilterBy] = useState('name');
  useEffect(() => {
    if (setActiveItem) {
      if (location.pathname.includes('/dashboard/nominations')) {
        setActiveItem('nominations');
      } else if (location.pathname.includes('/dashboard/featured')) {
        setActiveItem('athletes');
      } else if (location.pathname.includes('/dashboard/analytics')) {
        setActiveItem('analytics');
      } else if (location.pathname.includes('/dashboard/email-list')) {
        setActiveItem('email-list');
      }
    }
  }, [location.pathname, setActiveItem]);
  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    const [analyticsResult, totalsResult, standoutResult, standoutTotalsResult] = await Promise.all([
      getAllAnalytics(),
      getAnalyticsTotals(),
      getAllStandoutAnalytics(),
      getStandoutAnalyticsTotals()
    ]);

    if (analyticsResult.success) setAnalytics(analyticsResult.data || []);
    if (totalsResult.success) setTotals(totalsResult.data);
    if (standoutResult.success) setStandoutAnalytics(standoutResult.data || []);
    if (standoutTotalsResult.success) setStandoutTotals(standoutTotalsResult.data);
  };

  // Format numbers for display
  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num?.toString() || '0';
  };

  const statsCards = [
    {
      id: 1,
      title: 'Total Views',
      value: formatNumber(totals.views),
      unique: `${formatNumber(totals.unique_views)} unique`,
      icon: '/assets/images/eye-icon.png',
    },
    {
      id: 2,
      title: 'Share',
      value: formatNumber(totals.shares),
      unique: `${formatNumber(totals.unique_shares)} unique`,
      icon: '/assets/images/share-icon.png',
    },
    {
      id: 3,
      title: 'Link Copies',
      value: formatNumber(totals.link_copies),
      unique: `${formatNumber(totals.unique_link_copies)} unique`,
      icon: '/assets/images/link-icon.png',
    },
    {
      id: 4,
      title: 'Total Athletes Recognized',
      value: formatNumber(analytics.length),
      unique: `${analytics.length} spotlight athlete${analytics.length !== 1 ? 's' : ''}`,
      icon: '/assets/images/athlete-icon.png',
    },
    {
      id: 5,
      title: 'Downloads',
      value: formatNumber(totals.downloads),
      unique: `${formatNumber(totals.unique_downloads)} unique`,
      icon: '/assets/images/download-icon.png',
    },
  ];

  // Sort analytics based on filter
  const sortedAnalytics = [...analytics].sort((a, b) => {
    if (filterBy === 'name') return (a.spotlight?.athlete_name || '').localeCompare(b.spotlight?.athlete_name || '');
    if (filterBy === 'sport') return (a.spotlight?.primary_sport || '').localeCompare(b.spotlight?.primary_sport || '');
    if (filterBy === 'status') return (a.spotlight?.status || '').localeCompare(b.spotlight?.status || '');
    return 0;
  });

  const sortedStandoutAnalytics = [...standoutAnalytics].sort((a, b) => {
    if (standoutFilterBy === 'name') return (a.standout?.athlete_name || '').localeCompare(b.standout?.athlete_name || '');
    if (standoutFilterBy === 'sport') return (a.standout?.sport || '').localeCompare(b.standout?.sport || '');
    if (standoutFilterBy === 'week') return (a.standout?.week_label || '').localeCompare(b.standout?.week_label || '');
    return 0;
  });

  // Aggregate standout analytics by organization
  const orgAnalytics = Object.values(
    standoutAnalytics.reduce((acc, data) => {
      const orgName = data.standout?.org_name || 'Unknown Organization';
      if (!acc[orgName]) {
        acc[orgName] = { org_name: orgName, athletes: new Set(), views: 0, shares: 0, link_copies: 0, downloads: 0 };
      }
      acc[orgName].views += data.views || 0;
      acc[orgName].shares += data.shares || 0;
      acc[orgName].link_copies += data.link_copies || 0;
      acc[orgName].downloads += data.downloads || 0;
      if (data.standout?.athlete_name) acc[orgName].athletes.add(data.standout.athlete_name);
      return acc;
    }, {})
  ).map(org => ({ ...org, athlete_count: org.athletes.size }))
    .sort((a, b) => b.views - a.views);

  const standoutStatsCards = [
    { id: 1, title: 'Total Views', value: formatNumber(standoutTotals.views), unique: `${formatNumber(standoutTotals.unique_views)} unique`, icon: '/assets/images/eye-icon.png' },
    { id: 2, title: 'Share', value: formatNumber(standoutTotals.shares), unique: `${formatNumber(standoutTotals.unique_shares)} unique`, icon: '/assets/images/share-icon.png' },
    { id: 3, title: 'Link Copies', value: formatNumber(standoutTotals.link_copies), unique: `${formatNumber(standoutTotals.unique_link_copies)} unique`, icon: '/assets/images/link-icon.png' },
    { id: 4, title: 'Total Athletes Recognized', value: formatNumber(standoutAnalytics.length), unique: `${standoutAnalytics.length} standout athlete${standoutAnalytics.length !== 1 ? 's' : ''}`, icon: '/assets/images/athlete-icon.png' },
    { id: 5, title: 'Downloads', value: formatNumber(standoutTotals.downloads), unique: `${formatNumber(standoutTotals.unique_downloads)} unique`, icon: '/assets/images/download-icon.png' },
  ];

  return (
    <div className="analytics-screen">
      <DashboardSidebar
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        setActiveScreen={setActiveScreen}
      />
      <div className="analytics-screen-content">
        <DashboardHeader
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          title="Analytics Dashboard"
          subtitle="View engagement metrics for all published spotlights"
        />
        <Container fluid className="analytics-screen-container">
          {/* Filter Section */}
          <div className="analytics-filter-section">
            <label className="analytics-filter-label">Filter by Spotlight:</label>
            <div className="analytics-filter-wrapper">
              <select 
                className="analytics-filter-select"
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
              >
                <option value="name">By Name</option>
                <option value="sport">By Sport</option>
              </select>
              <img src="/assets/images/arrow-down-icon.png" alt="" className="analytics-filter-arrow" />
            </div>
          </div>

          {/* Stats Cards */}
          <div className="analytics-stats-grid">
            {statsCards.map((stat) => (
              <div key={stat.id} className="analytics-stat-card">
                <div className="analytics-stat-header">
                  <span className="analytics-stat-title">{stat.title}</span>
                  <img src={stat.icon} alt={stat.title} className="analytics-stat-icon" />
                </div>
                <div className="analytics-stat-value">{stat.value}</div>
                <div className="analytics-stat-unique">{stat.unique}</div>
              </div>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Loading analytics...</p>
            </div>
          )}

          {/* Analytics Table */}
          {!loading && (
          <div className="analytics-table-section">
            <div className="analytics-table-wrapper">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Athlete</th>
                    <th>Sports</th>
                    <th>Status</th>
                    <th>Views</th>
                    <th>Link Copies</th>
                    <th>Downloads</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAnalytics.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        No analytics data available yet
                      </td>
                    </tr>
                  ) : (
                    sortedAnalytics.map((data) => (
                      <tr key={data.id}>
                        <td>{data.spotlight?.athlete_name || 'Unknown'}</td>
                        <td>{data.spotlight?.primary_sport || 'N/A'}</td>
                        <td>
                          <span className={`analytics-status analytics-status-${(data.spotlight?.status || 'draft').toLowerCase()}`}>
                            <span className="analytics-status-dot"></span>
                            {data.spotlight?.status || 'Draft'}
                          </span>
                        </td>
                        <td>{formatNumber(data.views)} ({formatNumber(data.unique_views)} unique)</td>
                        <td>{formatNumber(data.link_copies)}</td>
                        <td>{formatNumber(data.downloads)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Analytics Rules */}
          <div className="analytics-rules-section">
            <h3 className="analytics-rules-title">Analytics Rules</h3>
            <ul className="analytics-rules-list">
              <li>Primary metric: total counts for all events</li>
              <li>Unique counts shown when user identifiers exist</li>
              <li>Analytics persist across unpublish actions</li>
              <li>Analytics stop incrementing when a Spotlight is archived</li>
              <li>Archived Spotlights are excluded from this dashboard</li>
            </ul>
          </div>

          {/* ── STANDOUTS ANALYTICS ── */}
          <div className="analytics-section-divider" style={{ margin: '40px 0 24px', borderTop: '1px solid rgba(216,161,52,0.25)' }} />

          <div style={{ marginBottom: '8px' }}>
            <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: '700', margin: '0 0 4px' }}>Standouts Analytics</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0 }}>Engagement metrics for all published standouts</p>
          </div>

          {/* Standout Filter */}
          <div className="analytics-filter-section" style={{ marginTop: '20px' }}>
            <label className="analytics-filter-label">Filter by Standout:</label>
            <div className="analytics-filter-wrapper">
              <select
                className="analytics-filter-select"
                value={standoutFilterBy}
                onChange={(e) => setStandoutFilterBy(e.target.value)}
              >
                <option value="name">By Name</option>
                <option value="sport">By Sport</option>
                <option value="week">By Week</option>
                <option value="org">By Organization</option>
              </select>
              <img src="/assets/images/arrow-down-icon.png" alt="" className="analytics-filter-arrow" />
            </div>
          </div>

          {/* Standout Stats Cards */}
          <div className="analytics-stats-grid">
            {standoutStatsCards.map((stat) => (
              <div key={stat.id} className="analytics-stat-card">
                <div className="analytics-stat-header">
                  <span className="analytics-stat-title">{stat.title}</span>
                  <img src={stat.icon} alt={stat.title} className="analytics-stat-icon" />
                </div>
                <div className="analytics-stat-value">{stat.value}</div>
                <div className="analytics-stat-unique">{stat.unique}</div>
              </div>
            ))}
          </div>

          {/* Standout Analytics Table */}
          {!loading && standoutFilterBy !== 'org' && (
            <div className="analytics-table-section">
              <div className="analytics-table-wrapper">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Athlete</th>
                      <th>Sport</th>
                      <th>Week</th>
                      <th>Views</th>
                      <th>Shares</th>
                      <th>Link Copies</th>
                      <th>Downloads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStandoutAnalytics.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          No standout analytics data yet
                        </td>
                      </tr>
                    ) : (
                      sortedStandoutAnalytics.map((data) => (
                        <tr key={data.id}>
                          <td>{data.standout?.athlete_name || 'Unknown'}</td>
                          <td>{data.standout?.sport || 'N/A'}</td>
                          <td>{data.standout?.week_label || '—'}</td>
                          <td>{formatNumber(data.views)} ({formatNumber(data.unique_views)} unique)</td>
                          <td>{formatNumber(data.shares)}</td>
                          <td>{formatNumber(data.link_copies)}</td>
                          <td>{formatNumber(data.downloads)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* League / Org Stats Table */}
          {!loading && standoutFilterBy === 'org' && (
            <div className="analytics-table-section">
              <div className="analytics-table-wrapper">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Athletes Recognized</th>
                      <th>Total Views</th>
                      <th>Total Shares</th>
                      <th>Link Copies</th>
                      <th>Downloads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgAnalytics.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          No organization data yet
                        </td>
                      </tr>
                    ) : (
                      orgAnalytics.map((org) => (
                        <tr key={org.org_name}>
                          <td>{org.org_name}</td>
                          <td>{org.athlete_count}</td>
                          <td>{formatNumber(org.views)}</td>
                          <td>{formatNumber(org.shares)}</td>
                          <td>{formatNumber(org.link_copies)}</td>
                          <td>{formatNumber(org.downloads)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Container>
      </div>
    </div>
  );
};

export default AnalyticsScreen;
